import React from 'react';
import { TrendingDown, TrendingUp, ArrowRight, Shield } from 'lucide-react';

const RiskHeatmap = ({ occupations, onOccupationClick }) => {
  // Get risk level color
  const getRiskColor = (score) => {
    if (score <= 25) return 'bg-red-600';
    if (score <= 40) return 'bg-orange-500';
    if (score <= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'declining':
      case 'collapsing':
        return <TrendingDown className="w-3 h-3 text-white opacity-75" />;
      case 'growing':
      case 'evolving':
        return <TrendingUp className="w-3 h-3 text-white opacity-75" />;
      case 'bifurcating':
        return <ArrowRight className="w-3 h-3 text-white opacity-75" />;
      default:
        return <Shield className="w-3 h-3 text-white opacity-75" />;
    }
  };

  // Sort by risk score (lowest to highest)
  const sortedOccupations = [...occupations].sort((a, b) => a.riskScore - b.riskScore);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Occupation Risk Heatmap</h2>
        <p className="text-gray-600 text-sm">
          Visual overview of automation risk across all 22 SOC major occupation groups
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-600"></div>
          <span className="text-gray-700">Critical (0-25)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500"></div>
          <span className="text-gray-700">High (26-40)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500"></div>
          <span className="text-gray-700">Moderate (41-60)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-gray-700">Lower (61-100)</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {sortedOccupations.map((occupation) => {
          const colorClass = getRiskColor(occupation.riskScore);
          
          return (
            <div
              key={occupation.id}
              className={`${colorClass} rounded-lg p-3 text-white transition-transform hover:scale-105 cursor-pointer`}
              title={`Click to view ${occupation.title} details`}
              onClick={() => onOccupationClick && onOccupationClick(occupation.id)}
            >
              {/* Risk Score - Large */}
              <div className="text-3xl font-bold mb-1">
                {occupation.riskScore}
              </div>
              
              {/* Occupation Title - Small */}
              <div className="text-xs font-semibold mb-2 line-clamp-2 min-h-[2rem]">
                {occupation.title}
              </div>
              
              {/* Bottom Row: SOC Code, Trend, Change */}
              <div className="flex items-center justify-between text-xs opacity-90">
                <span className="font-mono">{occupation.socCode.split('-')[0]}</span>
                <div className="flex items-center gap-1">
                  {getTrendIcon(occupation.trend)}
                  {occupation.scoreChange !== 0 && (
                    <span className="font-bold">
                      {occupation.scoreChange > 0 ? '+' : ''}{occupation.scoreChange}
                    </span>
                  )}
                </div>
              </div>

              {/* Recent Layoffs Indicator */}
              {occupation.recentLayoffs > 0 && (
                <div className="mt-2 pt-2 border-t border-white/30 text-xs font-semibold">
                  {occupation.recentLayoffs > 1000 
                    ? `${(occupation.recentLayoffs / 1000).toFixed(1)}K layoffs`
                    : `${occupation.recentLayoffs} layoffs`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Key Insights */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <span className="text-blue-600">📊</span>
          Key Insights This Week
        </h3>
        <ul className="space-y-1 text-sm text-gray-700">
          {occupations.filter(o => o.scoreChange !== 0).length > 0 && (
            <li>
              <strong>{occupations.filter(o => o.scoreChange !== 0).length} occupations</strong> changed risk scores this week
            </li>
          )}
          {occupations.filter(o => o.recentLayoffs > 0).length > 0 && (
            <li>
              <strong>{occupations.filter(o => o.recentLayoffs > 0).reduce((sum, o) => sum + o.recentLayoffs, 0).toLocaleString()} total layoffs</strong> reported in the last 90 days
            </li>
          )}
          <li>
            <strong>{occupations.filter(o => o.riskScore <= 40).length} occupation groups</strong> at critical or high risk (affecting {((occupations.filter(o => o.riskScore <= 40).reduce((sum, o) => sum + o.employment, 0) / 1000000).toFixed(1))}M workers)
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RiskHeatmap;